import { useTranslation } from 'react-i18next'
import { FloatingTree } from '@floating-ui/react'
import classnames from 'classnames'

import { stopPropagation } from '../../../utils/events'
import { isImageFile, isMediaFile, isVideoFile } from '../../../utils/file'
import { FileIconPreview } from '../../FileIcon'
import { FloatingTooltip, FloatingTooltipContentText } from '../../Floating'
import { ICON_COLOR, IconNG } from '../../Icon'
import { Skeleton } from '../../Skeleton'
import Text from '../../Text'
import { getFileSize } from '../File.utils'
import { FileImagePreviewError } from '../FileImagePreviewError'
import { FilePreviewErrorTooltipText } from '../FilePreviewErrorTooltipText'
import { FilePreviewImage } from '../FilePreviewImage'
import { FilePreviewOverlay } from '../FilePreviewOverlay'
import { FilePreviewVideo } from '../FilePreviewVideo'

import { getFileName, getFilePreviewImageSrc } from './FilePreview.utils'

import * as styles from './FilePreview.module.css'

import ErrorIcon from '../../../icons/error.svg'

function FilePreview({
  ErrorImagePreview = FileImagePreviewError,
  ErrorPreviewImageOverlay = FilePreviewOverlay,
  Overlay = FilePreviewOverlay,
  additionalInfo,
  allowAvatarSelection,
  allowRemove = true,
  className,
  disabled,
  errorImagePreviewProps,
  file,
  fileRef,
  getImageSrc = getFilePreviewImageSrc,
  isSelected,
  isSkeleton,
  overlayProps,
  setAvatar,
  tooltip,
  tooltipContentStyles,
  onClick,
  onRemoveFile,
}) {
  const { t } = useTranslation()

  // #TODO make styles via composes
  // in `FilePreviewImage`, `FilePreviewVideo`
  const fileClassName = classnames(
    styles.file,
    {
      [styles.disabled]: disabled,
      [styles.loading]: file.uploading,
    },
    className
  )

  function handleRemoveItem(event) {
    stopPropagation(event)

    onRemoveFile?.(file.id)
  }

  function handleSelectAvatar(event) {
    stopPropagation(event)

    setAvatar?.(isSelected ? null : file.id)
  }

  function handleClickPreview() {
    if (!disabled) {
      onClick?.(file.id)
    }
  }

  const fileTooltip = (
    <FloatingTooltipContentText
      additionalText={t('FilePreview.TooltipClickToOpenTextKey')}
      text={file.filename || file.name}
    />
  )

  if (!file.uploading && isMediaFile(file)) {
    if (isImageFile(file)) {
      return (
        <FilePreviewImage
          ErrorImagePreview={ErrorImagePreview}
          className={fileClassName}
          errorPreviewImageProps={{
            ...errorImagePreviewProps,
            Overlay: ErrorPreviewImageOverlay,
            className: fileClassName,
            disabled,
            tooltip: fileTooltip,
            onRemove: allowRemove ? handleRemoveItem : null,
          }}
          filename={getFileName(file.filename || file.name)}
          fileSize={getFileSize({ file })}
          src={getImageSrc(file)}
          onClick={handleClickPreview}
        >
          <Overlay
            {...overlayProps}
            allowAvatarSelection={allowAvatarSelection}
            className={styles.imageOverlay}
            disabled={disabled}
            error={file.error}
            filename={file.filename || file.name}
            selected={isSelected}
            tooltip={fileTooltip}
            onRemove={allowRemove ? handleRemoveItem : null}
            onSelectAvatar={handleSelectAvatar}
          />
        </FilePreviewImage>
      )
    }

    if (isVideoFile(file)) {
      return (
        <FilePreviewVideo
          className={fileClassName}
          data={file}
          disabled={disabled}
          onClick={handleClickPreview}
        >
          <Overlay
            {...overlayProps}
            disabled={disabled}
            error={file.error}
            filename={file.filename}
            tooltip={fileTooltip}
            onRemove={allowRemove ? handleRemoveItem : null}
          />
        </FilePreviewVideo>
      )
    }
  }

  const loaderBoxClassName = classnames(styles.loaderBox)

  const loaderClass = classnames(styles.loader, {
    [styles.loaderActive]: !file.error,
  })

  const fileInfoClassName = classnames(styles.fileInfo, {
    [styles.error]: file.error,
    [styles.loading]: file.uploading,
  })

  return (
    <FloatingTree>
      {isSkeleton ? (
        <Skeleton className={styles.skeleton} />
      ) : (
        <FloatingTooltip
          content={
            tooltip ||
            (file.error ? <FilePreviewErrorTooltipText file={file} /> : null) ||
            fileTooltip
          }
          contentStyles={tooltipContentStyles}
          disabled={disabled}
        >
          <div
            className={fileClassName}
            ref={fileRef}
            onClick={handleClickPreview}
          >
            <FileIconPreview className={styles.fileIcon} file={file} />
            <Text className={styles.text}>
              {getFileName(file.filename || file.name)}
            </Text>
            <div className={fileInfoClassName}>
              {file.uploading || file.error ? (
                <div className={loaderBoxClassName}>
                  {file.error ? (
                    <div className={styles.errorIconBlock}>
                      <IconNG Icon={ErrorIcon} color={ICON_COLOR.White} />
                      <span className={styles.errorText}>
                        {t('Error.FileUploadKey')}
                      </span>
                    </div>
                  ) : (
                    <span
                      className={loaderClass}
                      style={{ '--end': 'forwards' }}
                    />
                  )}
                </div>
              ) : (
                <span className={styles.size}>
                  {additionalInfo || getFileSize({ file })}
                </span>
              )}
            </div>
            <Overlay
              {...overlayProps}
              allowAvatarSelection={allowAvatarSelection}
              disabled={disabled}
              error={file.error}
              filename={file.filename || file.name}
              selected={isSelected}
              onRemove={allowRemove ? handleRemoveItem : null}
              onSelectAvatar={handleSelectAvatar}
            />
          </div>
        </FloatingTooltip>
      )}
    </FloatingTree>
  )
}

export default FilePreview
